import { useId } from "react";
import type { TextAreaProps as RATextAreaProps } from "react-aria-components";
import {
  TextArea as RATextArea,
  Label as RALabel,
} from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import InputErrorMessage from "./InputErrorMessage";

const textarea = tv({
  base: "space-y-0.5",
  variants: {
    variant: {
      bordered: { inputWrapper: "border border-border" },
      borderless: { inputWrapper: "" },
      flat: { inputWrapper: "px-0!" },
    },
    color: {
      background: { inputWrapper: "bg-background" },
      background2: { inputWrapper: "bg-background-2" },
      secondary: { inputWrapper: "bg-secondary" },
      tertiary: { inputWrapper: "bg-tertiary" },
    },
    isInvalid: {
      true: { inputWrapper: "border-danger" },
    },
    size: {
      md: { inputWrapper: "p-4" },
    },
  },
  defaultVariants: {
    variant: "borderless",
    color: "secondary",
    size: "md",
  },
  slots: {
    description: "mt-1! ps-1 text-xs text-text-secondary",
    input: `
      w-full rounded-lg bg-transparent text-sm/6 transition-colors duration-100
      outline-none
      placeholder:text-text-secondary
      focus-within:text-text-contrast
    `,
    inputWrapper: `
      flex flex-row items-center gap-4 rounded-lg text-text-secondary
      has-disabled:bg-transparent!
    `,
    label: "text-sm text-text-tertiary",
  },
});

export interface TextAreaProps
  extends
    Omit<RATextAreaProps, "color" | "size">,
    VariantProps<typeof textarea> {
  readonly label?: string;
  readonly className?: string;
  readonly description?: string;
  readonly errorMessage?: string;
}

export default function TextArea({
  label,
  variant,
  color,
  isInvalid,
  size,
  className,
  ref,
  description,
  errorMessage,
  ...props
}: TextAreaProps & { readonly ref?: React.Ref<HTMLTextAreaElement> }) {
  const generatedId = useId();
  const textAreaId = props.id ?? generatedId;
  const {
    inputWrapper,
    description: _description,
    label: _label,
    input,
    base,
  } = textarea({ variant, color, isInvalid, size });

  return (
    <div className={base({ className })}>
      {label && (
        <div>
          <RALabel htmlFor={textAreaId} className={_label()}>
            {label}
          </RALabel>
        </div>
      )}
      <div className={inputWrapper()}>
        <RATextArea {...props} id={textAreaId} ref={ref} className={input()} />
      </div>
      {description && <div className={_description()}>{description}</div>}
      {isInvalid && errorMessage && (
        <InputErrorMessage>{errorMessage}</InputErrorMessage>
      )}
    </div>
  );
}
